import React, { useCallback } from 'react';
import { Tooltip } from '@dopomogai/ui';
import { Sprout, Sparkles, Zap, Star } from 'lucide-react';

type TierKey = 'trainee' | 'junior' | 'middle' | 'senior';

export type TierOption = {
  key: TierKey;
  title: string;
  oneLiner?: string;
};

type Props = {
  value: TierKey;
  onChange: (tier: TierKey) => void;
  tiers?: TierOption[];
  className?: string;
  showMicrocopy?: boolean;
};

const DEFAULTS: TierOption[] = [
  { key: 'trainee', title: 'Trainee', oneLiner: 'Core chat + 1–2 channels' },
  { key: 'junior', title: 'Junior', oneLiner: 'Adds social DMs, WhatsApp, basic CRM/commerce' },
  { key: 'middle', title: 'Middle', oneLiner: 'Deeper CRM/commerce, analytics, recommendations' },
  { key: 'senior', title: 'Senior', oneLiner: 'Advanced automations, multi‑channel, read/write' },
];

function tierIcon(key: TierKey) {
  switch (key) {
    case 'trainee':
      return <Sprout size={14} />;
    case 'junior':
      return <Sparkles size={14} />;
    case 'middle':
      return <Zap size={14} />;
    case 'senior':
      return <Star size={14} />;
  }
}

function tierAccent(key: TierKey) {
  switch (key) {
    case 'senior':
      return { bg: 'bg-violet-200', text: 'text-violet-900', hover: 'hover:bg-violet-300' };
    case 'middle':
      return { bg: 'bg-sky-200', text: 'text-sky-900', hover: 'hover:bg-sky-300' };
    case 'junior':
      return { bg: 'bg-emerald-200', text: 'text-emerald-900', hover: 'hover:bg-emerald-300' };
    case 'trainee':
    default:
      return { bg: 'bg-slate-200', text: 'text-slate-900', hover: 'hover:bg-slate-300' };
  }
}

export const TierSelector: React.FC<Props> = ({
  value,
  onChange,
  tiers = DEFAULTS,
  className,
  showMicrocopy = true,
}) => {
  const keys: TierKey[] = ['trainee', 'junior', 'middle', 'senior'];
  const map = new Map(tiers.map((t) => [t.key, t]));
  const selected = map.get(value) || DEFAULTS.find((t) => t.key === value) || DEFAULTS[1];

  const handleKey = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const idx = keys.indexOf(value);
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        onChange(keys[Math.min(keys.length - 1, idx + 1)]);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        onChange(keys[Math.max(0, idx - 1)]);
      }
    },
    [onChange, value]
  );

  return (
    <div className={className}>
      {/* Segmented control */}
      <div
        role="tablist"
        aria-label="Choose a tier"
        onKeyDown={handleKey}
        className={[
          'w-full border-2 border-black rounded-xl bg-white p-1',
          'grid grid-cols-2 sm:grid-cols-4 gap-1',
        ].join(' ')}
      >
        {keys.map((k) => {
          const active = value === k;
          const opt = map.get(k) || DEFAULTS.find((t) => t.key === k)!;
          const accent = tierAccent(k);

          const btn = (
            <button
              key={k}
              role="tab"
              aria-selected={active}
              aria-controls={`tier-panel-${k}`}
              onClick={() => onChange(k)}
              className={[
                'border-2 border-black rounded-lg',
                'flex items-center justify-center gap-1.5',
                'px-3 py-2',
                'transition-all duration-150 transform',
                'hover:-translate-x-px hover:-translate-y-px hover:shadow-[2px_2px_0_0_rgb(17,24,39)]',
                active ? `${accent.bg} ${accent.text}` : 'bg-slate-50 text-slate-900',
              ].join(' ')}
            >
              <span className="opacity-80">{tierIcon(k)}</span>
              <span className="text-sm font-extrabold">{opt.title}</span>
            </button>
          );

          // Tooltip only on non-active when viewport is wide; simple approach: always wrap,
          // Tooltip component itself can decide based on media queries if needed.
          return opt.oneLiner ? (
            <Tooltip key={k} text={opt.oneLiner}>
              {btn}
            </Tooltip>
          ) : (
            btn
          );
        })}
      </div>

      {/* Microcopy for the selected tier */}
      {showMicrocopy && selected?.oneLiner && (
        <div
          id={`tier-panel-${selected.key}`}
          role="tabpanel"
          aria-live="polite"
          className="mt-2 text-sm text-slate-700"
        >
          <span className="font-semibold text-slate-900">{selected.title}:</span> {selected.oneLiner}
        </div>
      )}
    </div>
  );
};

export default TierSelector;
